<template>
  <div v-if="loading" class="d-flex justify-center">
    <v-progress-circular :size="100" color="primary" indeterminate>
    </v-progress-circular>
  </div>
  <div v-else>

    <validation-observer ref="observer" v-slot="{ invalid }">
      <div class="d-flex align-items-center justify-content-between default-title p-20-px">
        <div class="d-flex align-items-center">
          <button icon @click="routeToContentLibrary()">
            <v-icon>mdi-arrow-left</v-icon>
          </button>
          <div class="ml-2">{{$t('ContentLibraryView.EditMedia')}}</div>
        </div>
        <div class="d-flex">
          <v-btn class="btn-default mr-2" uppercase @click="saveMedia()" :loading="saveLoading" :disabled="saveLoading || deleteLoading || invalid || isInvalidFile || !media.categoryIds.length
          || ((isVideo && !selectedVideo && !media.videoUrl) || (!isVideo && !selectedPdf && !media.pdfUrl))">
             {{$t('ContentLibraryView.SaveChanges')}}
          </v-btn>

          <v-btn v-if="canDeleteMedia" class="btn-default" uppercase @click="deleteMedia" :loading="deleteLoading" :disabled="saveLoading">
            {{$t('ContentLibraryView.DeleteMedia')}}
          </v-btn>
        </div>
      </div>
      <div class="cards-wrapper-form row m-0">
        <div class="d-flex col-md-3 col-xs-12 card-padding">
          <v-card class="w-100">

            <div class="d-flex justify-content-center align-items-center image-upload">
              <v-img :src="media.imageUrl" height="280" min-height="320"></v-img>
              <div v-if="loadingImage" class="position-absolute text-center mt-20 top-0 left-0">
                <v-progress-circular :size="100" color="primary" indeterminate></v-progress-circular>
              </div>
            </div>

            <div class="float-right">
              <v-btn icon @click="openGalleryPhotos()">
                <v-icon class="ignore-readonly" color="#3F51B5">mdi-image-multiple</v-icon>
              </v-btn>

              <v-btn icon @click="$refs.inputUpload.click()">
                <v-icon color="#3F51B5">mdi-camera</v-icon>
              </v-btn>
              <input v-show="false" ref="inputUpload" type="file" accept="image/*" @change="saveImage" />
            </div>

            <div class="w-100 p-15-px pt-6" color="primary">
              <validation-provider v-slot="{ errors }" name="MediaName" rules="required|max:25">
                <v-text-field v-model="media.name" :counter="25" :error-messages="errors" :label="$t('ContentLibraryView.MediaName') + '*'"
                  @change="onMediaChange"></v-text-field>
              </validation-provider>
              <v-switch v-model="media.isEnabled" @change="onMediaChange"
                :label="media.isEnabled ? $t('GeneralTranslations.Enable'): $t('GeneralTranslations.Disable')"></v-switch>
            </div>
          </v-card>
        </div>

        <div class="d-flex col-md-9 col-xs-12 card-padding">
          <v-card class="d-flex flex-column p-20-px w-100">

            <div class="row m-0">
              <div class="d-flex flex-column col-md-6 pr-50-px">
                <validation-provider v-slot="{ errors }" name="Category">
                  <v-select multiple v-model="media.categoryIds" :error-messages="errors" :items="categories"
                    :rules="[(v) => v && v.length >= 1 || $t('ContentLibraryView.MinCategoryMessage')]" item-text="name"
                    item-value="id" class="pb-20-px" label="Categories*" @change="onCategoryChange" required></v-select>
                </validation-provider>

                <v-radio-group v-model="isVideo" row>
                  <v-radio label="Video" :value="true"></v-radio>
                  <v-radio label="PDF" :value="false"></v-radio>
                </v-radio-group>

                <div class="v-label theme--light pb-5">{{$t('ContentLibraryView.UploadFile')}} *</div>
                <div class="file-upload-wrapper">
                  <div class="file-upload-btn-wrapper">
                    <v-btn class="pl-12 file-upload-icon-btn" icon @click="$refs.inputFileUpload.click()">
                      <v-icon class="file-upload-icon">mdi-file-upload-outline</v-icon>
                    </v-btn>
                  </div>
                  <div v-if="isInvalidFile" class="invalid-file">
                    <v-icon>mdi-alert</v-icon>
                    <small class="pr-1">  {{$t('ContentLibraryView.InvalidFileFormat')}}</small>
                  </div>
                </div>
                <b v-if="fileSelected">
                  <v-icon color="#000">mdi-check-all</v-icon> {{$t('ContentLibraryView.Loaded')}}
                </b>
                <input v-if="isVideo" v-show="false" required type="file" accept="video/mp4,video/x-m4v,video/*"
                  ref="inputFileUpload" @change="onVideoSelect($event)" />
                <input v-if="!isVideo" v-show="false" required type="file" accept=".pdf" ref="inputFileUpload"
                  @change="onPdfSelect($event)" />

                <div v-if="isVideo" class="pl-9 v-label theme--light pt-5">or</div>
                <div v-if="isVideo" class="pt-9">
                  <validation-provider v-slot="{ errors }" name="Add Video URL" rules="url|max:500">
                    <v-text-field v-model="media.videoUrl" :counter="500" :error-messages="errors"
                      v-on:change="onVideoUrlChange" :label=" $t('ContentLibraryView.AddVideoURL ')"></v-text-field>
                  </validation-provider>
                </div>
              </div>

              <div class="d-flex flex-column col-md-6 pr-50-px">
                <validation-provider v-slot="{ errors }" name="Description" rules="required|max:500">
                  <v-textarea v-model="media.description" :error-messages="errors" :label="$t('GeneralTranslations.Description') + '*'"
                    @change="onMediaChange" :counter="500" required rows="4"></v-textarea>
                </validation-provider>

                <v-container v-if="isVideo && media.videoUrl">
                  <div class="pb-3 v-label theme--light pt-5">{{$t('ContentLibraryView.Preview')}}</div>

                  <video v-if="media.videoUrl && media.videoUrl.includes('firebasestorage.googleapis.com')" width="100%"
                    height="200px" :src="media.videoUrl" controls>
                    {{$t('GeneralTranslations.YourBrowserDoesNotSupportTheVideoTag')}}.
                  </video>

                  <youtube v-if="media.videoUrl && media.videoUrl.includes('youtube.com')"
                    :video-id="getYoutubeVideoId()">
                  </youtube>

                  <video-embed
                    v-if="media.videoUrl && media.videoUrl.includes('vimeo.com') || media.videoUrl && media.videoUrl.includes('dailymotion.com')"
                    :src="media.videoUrl"></video-embed>
                </v-container>

                <v-container v-if="!isVideo && media.pdfUrl">
                  <div class="pb-3 v-label theme--light pt-5">  {{$t('ContentLibraryView.Preview')}}</div>
                  <embed :src="media.pdfUrl" width="100%" height="200px" />
                </v-container>

              </div>
            </div>

          </v-card>
        </div>
      </div>
    </validation-observer>

    <ConfirmDlg ref="confirmationModal"></ConfirmDlg>

    <GalleryPhotosUploadDlg ref="GalleryPhotosUploadDlg" @uploadGalleryPhoto="uploadGalleryPhoto" />
  </div>
</template>

<script>
import { mapState } from "vuex";
import * as firebase from "firebase/app";
import { uploadImage, deleteFileByUrl, optimizePhotos } from "@/utils/upload-image";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import ConfirmDlg from "@/components/popup/confirm-dialog/ConfirmDlg.vue";
import { fileSizeToMB, validFileSize10MB } from "@/utils/fileSizeToMB";
const getYoutubeVideoId = require('get-youtube-id');
import GalleryPhotosUploadDlg from "@/views/staff/gallery/GalleryPhotosUploadDlg/GalleryPhotosUploadDlg.vue";
import {waitResizedImage} from "@/utils/wait-resized-image";
import {v4 as uuidv4} from "uuid";
import { hasAccess } from "@/utils/accessControl/accessControl";
import { AccessMatrixKeys } from "@/utils/accessControl/accessMatrixKeysEnum";

export default {
  name: "ContentLibraryView",
  components: {
    ValidationObserver,
    ValidationProvider,
    ConfirmDlg,
    GalleryPhotosUploadDlg
  },
  data() {
    return {
      loading: true,
      loadingImage: false,
      saveLoading: false,
      deleteLoading: false,
      selectedVideo: null,
      selectedPdf: null,
      selectedImage: null,
      isVideo: false,
      fileSelected: false,
      newVideoUrl: null,
      prevVideoUrl: null,
      prevPdfUrl: null,
      mediaChanged: false,
      isInvalidFile: false
    }
  },
  computed: {
    ...mapState({
      media: (state) => state.contentLibraryModule.contentItem,
      categories: (state) => state.contentLibraryModule.contentCategories,
      currentFacility: state => state.authModule.currentFacility,
      currentUser: state => state.authModule.user
    }),
    canDeleteMedia(){
      return hasAccess(this.currentUser.accessLevel, AccessMatrixKeys.MediaLibrary_MediaView_DeleteMedia)
    }
  },
  watch: {
    media(newValue) {
      this.prevVideoUrl = this.media.videoUrl
      this.prevPdfUrl = this.media.pdfUrl
      if (newValue.videoUrl != null && newValue.videoUrl != '') {
        this.isVideo = true
      }
      else {
        this.isVideo = false
      }
      this.loading = false
    }
  },
  async created() {
    await this.$store.dispatch("getContentItemById", this.$route.params.id)
    await this.$store.dispatch("getContentCategories")
  },
  async beforeRouteLeave(to, from, next) {
    if (this.mediaChanged) {
      this.$refs.confirmationModal
        .open(null, this.$i18n.messages[this.$i18n.locale]['ContentLibraryView.SkipChangesMessage'], false)
        .then(async (response) => {
          if (response && response.agree) {
            next()
          }
        })
    }
    else {
      next()
    }
  },
  methods: {
    getYoutubeVideoId() {
      return getYoutubeVideoId(this.media.videoUrl)
    },

    routeToContentLibrary() {
      this.$router.push({
        name: "ContentLibraryList",
        params: {
          page: this.$route.params.page,
          pagePer: this.$route.params.pagePer
        },
      })
    },

    onMediaChange() {
      this.mediaChanged = true
    },

    onCategoryChange(e) {
      this.media.categories = this.categories.filter(x => this.media.categoryIds.includes(x.id)).map(x => ({ id: x.id, name: x.name })) || []

      this.mediaChanged = true
    },

    onVideoSelect(event) {
      this.isInvalidFile = !['m4v', 'avi', 'mpg', 'mp4'].includes(this.getExtension(event.target.files[0].name).toLowerCase())
      if (!this.isInvalidFile) {
        this.fileSelected = true
        this.selectedPdf = null
        this.selectedVideo = event.target.files[0]
        this.onMediaChange()
      }
      else {
        this.fileSelected = false
      }
    },

    onPdfSelect(event) {
      this.isInvalidFile = this.getExtension(event.target.files[0].name).toLowerCase() != 'pdf'
      if (!this.isInvalidFile) {
        this.fileSelected = true
        this.selectedVideo = null
        this.selectedPdf = event.target.files[0]
        this.onMediaChange()
      }
      else {
        this.fileSelected = false
      }
    },

    onVideoUrlChange(value) {
      if (this.isVideo && value) {
        this.newVideoUrl = decodeURIComponent(value)
        this.fileSelected = false
      }
      if (this.selectedVideo) {
        this.selectedVideo = null
        this.$refs.inputFileUpload.value = null
      }
      this.onMediaChange()
    },

    async saveImage(e) {
      this.onMediaChange()
      this.saveLoading = true
      const file = await optimizePhotos(e.target.files[0], '');
      const fileSizeInMegabytes = fileSizeToMB(file?.size);
      const path = `${this.currentFacility}/contentLibrary/${this.media.id}/${uuidv4()}`
      const prevImagUrl = this.media.imageUrl;

      if (!validFileSize10MB(fileSizeInMegabytes)) {
        this.$refs.inputUpload.value = null;
        this.saveLoading = false;
        return;
      }

      uploadImage({
        path, file,
        onStart: () => {
          this.loadingImage = true
        },
        onComplete: async storage => {
          const uploadedImageUrl = await storage.snapshot.ref.getDownloadURL();
          const resizedImageRef = await firebase.storage().ref().child(`${path}_200x200`);

          try {
            const resizedImageUrl = await waitResizedImage(10, resizedImageRef);
            this.media.imageUrl = uploadedImageUrl
            this.media.thumbnailUrl = resizedImageUrl
          } catch (e) {
            this.media.imageUrl = uploadedImageUrl;
            this.media.thumbnailUrl = uploadedImageUrl;
          }

          this.loadingImage = false
          this.saveLoading = false

          await firebase.firestore().collection("contentLibrary").doc(this.media.id).update({
            imageUrl: this.media.imageUrl,
            thumbnailUrl: this.media.thumbnailUrl
          }).then(async () => {
            await deleteFileByUrl(prevImagUrl).then(() => { console.log(`prev image deleted`) })
          })
        }
      })
    },

    async saveMedia() {
      const isValid = await this.$refs.observer.validate()
      if (!isValid) return

      const promises = [];
      this.saveLoading = true
      this.mediaChanged = false

      const updateData = {
        name: this.media.name,
        categoryIds: this.media.categoryIds,
        categories: this.media.categories,
        description: this.media.description,
        isEnabled: this.media.isEnabled,
        dateTimeUpdated: firebase.firestore.Timestamp.fromDate(new Date()),
        imageUrl: this.media.imageUrl,
        thumbnailUrl: this.media?.thumbnailUrl ?? this.media.imageUrl
      }

      if (this.selectedVideo) {

        const videoPromise = this.uploadFile(this.selectedVideo, this.media.id)

        if (this.prevPdfUrl) await deleteFileByUrl(this.prevPdfUrl)

        if (this.prevVideoUrl) await deleteFileByUrl(this.prevVideoUrl)

        updateData.pdfUrl = null

        promises.push(videoPromise);

        console.log(`upload video, delete pdf`)
      }

      if (this.selectedPdf) {

        const pdfPromise = this.uploadFile(this.selectedPdf, this.media.id)

        if (this.prevPdfUrl) await deleteFileByUrl(this.prevPdfUrl)

        if (this.prevVideoUrl) await deleteFileByUrl(this.prevVideoUrl)

        updateData.videoUrl = null

        promises.push(pdfPromise)

        console.log(`upload pdf, delete video`)
      }

      if (this.newVideoUrl) {

        updateData.videoUrl = this.newVideoUrl

        updateData.pdfUrl = null

        if (this.prevVideoUrl) await deleteFileByUrl(this.prevVideoUrl)

        if (this.media.pdfUrl) await deleteFileByUrl(this.media.pdfUrl)

        console.log(`add video url, delete video`)
      }

      const mediaPromise = firebase.firestore().collection("contentLibrary").doc(this.media.id).update(updateData)

      promises.push(mediaPromise)

      console.log(`update content`)

      await Promise.all(promises).then(async () => {
        this.selectedVideo = null
        this.selectedPdf = null
        this.newVideoUrl = null
        await this.$store.dispatch("getContentItemById", this.$route.params.id)
        console.log("successfully updated media");
      }).catch(error => {
        console.error(`Error updating media: ${error}`)
      }).finally(() => {
        this.saveLoading = false
      })
    },

    async deleteMedia() {
      this.$refs.confirmationModal
        .open(null, this.$i18n.messages[this.$i18n.locale]['ContentLibraryView.DeleteMediaMessage'], false)
        .then(async (response) => {
          if (response && response.agree) {
            this.deleteLoading = true
            await this.$store.dispatch("deleteContent", this.media).then(async () => {
              this.deleteLoading = false
              this.$router.push("/contentLibrary")
            })
          }
        })
    },

    async uploadFile(file, contentId) {
      if (!file) return;

      try {
        const storageRef = firebase.storage().ref()
        const filesRef = storageRef.child(this.currentFacility).child('contentLibrary')

        let type = file.extension || file.type;
        if (type === 'pdf') {
          type = file.type;
        }

        const uploadFileRef = filesRef.child(contentId).child(`${file.name}`)

        return await uploadFileRef.put(file, { contentType: type }).then(async () => {
          return await uploadFileRef.getDownloadURL().then(async (url) => {
            const update = type.includes('pdf') ? { pdfUrl: url, videoUrl: null } : { videoUrl: url, pdfUrl: null }
            return await firebase.firestore().collection("contentLibrary").doc(contentId).update(update)
          })
        })

      } catch (error) {
        this.saveLoading = true
        return;
      }
    },

    getExtension(filename) {
      const parts = filename.split('.')
      return parts[parts.length - 1]
    },

    openGalleryPhotos() {
      this.$refs.GalleryPhotosUploadDlg.open();
    },

    uploadGalleryPhoto(image) {
      this.media.imageUrl = image.image;
      this.documentImage = image.image;
      this.media.thumbnailUrl = image?.thumbnailUrl ?? image.image;
    },
  },
};
</script>

<style scoped>
.file-upload-icon {
  color: #3F51B5 !important;
  font-size: 50px !important;
}

::v-deep .file-upload-icon-btn:before {
  opacity: 0 !important;
}

::v-deep .v-ripple__container {
  opacity: 0 !important;
}

::v-deep iframe {
  max-width: 400px !important;
}

.invalid-file {
  color: red;
  padding-left: 60px;
}

::v-deep .invalid-file>.v-icon {
  color: red !important;
  font-size: 14px !important;
}

.invalid-file,
.file-upload-btn-wrapper {
  display: inline-block;
}
</style>